/**
 * Guest — Public Digital Menu (no auth).
 *
 * Route: /menu/:slug
 *  - If outlet not found OR menu_enabled=false → "Menu Coming Soon" card.
 *  - If exactly one published menu → auto-open that menu.
 *  - Otherwise → menu-selector landing.
 *
 * Route: /menu/:slug/:menuId
 *  - Individual menu view (category nav + search).
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, ChevronLeft, Utensils, Wine, Coffee, Sandwich,
         Moon, Cake, Baby, Leaf, Sparkles, Users, Bed } from 'lucide-react';
import { VegDot } from './AdminOutletMenus';
import { CategorySection } from '../components/menu/CategorySection';
import { FloatingCategoryFAB } from '../components/menu/FloatingCategoryFAB';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  utensils: Utensils, wine: Wine, coffee: Coffee, sandwich: Sandwich,
  moon: Moon, cake: Cake, baby: Baby, leaf: Leaf, sparkles: Sparkles,
  users: Users, bed: Bed,
};

export default function GuestMenu() {
  const { slug, menuId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/public/menu/outlet-by-slug/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Menu not available'))
      .finally(() => setLoading(false));
  }, [slug]);

  const theme = data?.theme || {
    primary: 'var(--color-primary)', secondary: '#C9A227', background: '#FBF7EC',
  };

  // Auto-open if exactly one menu and no menu chosen in URL
  useEffect(() => {
    if (!menuId && data?.menus?.length === 1) {
      navigate(`/menu/${slug}/${data.menus[0].id}`, { replace: true });
    }
  }, [data, menuId, slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{ background: theme.background }}>
        <div className="text-gray-500">Loading menu…</div>
      </div>
    );
  }

  if (error) {
    const isComingSoon = /coming soon/i.test(error);
    return (
      <div className="min-h-screen flex items-center justify-center p-6"
           style={{ background: theme.background }}>
        <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-lg p-10 space-y-4"
             data-testid="menu-unavailable">
          <div className="text-5xl">{isComingSoon ? '🍽️' : '⚠️'}</div>
          <h1 className="text-2xl font-semibold" style={{ color: theme.primary }}>
            {isComingSoon ? 'Menu Coming Soon' : 'Menu Not Available'}
          </h1>
          <p className="text-sm text-gray-500">
            {isComingSoon
              ? 'We are preparing the digital menu. Please check back shortly.'
              : error}
          </p>
        </div>
      </div>
    );
  }

  if (menuId) {
    return <SingleMenuView slug={slug} menuId={menuId} outletMeta={data} theme={theme} />;
  }

  return <MenuSelector data={data} theme={theme} slug={slug} />;
}

/* ==================== Selector landing ==================== */

function MenuSelector({ data, theme, slug }) {
  const { outlet, welcome_message, menus } = data;
  return (
    <div className="min-h-screen relative pb-16" style={{ background: theme.background }}>
      <div className="max-w-2xl mx-auto px-5 pt-10 pb-14">
        <div className="text-center space-y-4" data-testid="menu-selector">
          {outlet.logo_url && (
            <div className="mx-auto inline-flex items-center justify-center p-2 rounded-2xl bg-white shadow-md"
                 style={{ boxShadow: '0 6px 18px rgba(20,61,48,0.10)' }}
                 data-testid="outlet-logo-container">
              <img src={outlet.logo_url} alt={outlet.name}
                   className="block object-contain rounded-xl max-h-[72px] max-w-[72px] sm:max-h-[96px] sm:max-w-[96px] h-auto w-auto"
                   data-testid="outlet-logo" />
            </div>
          )}
          <div className="text-xs uppercase tracking-[0.3em]" style={{ color: theme.secondary }}>
            Welcome to
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl" style={{ color: theme.primary }}>
            {outlet.name}
          </h1>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            {welcome_message || 'Please choose a menu'}
          </p>
          {outlet.city && (
            <div className="text-xs text-gray-500">{outlet.city}</div>
          )}
        </div>

        {menus.length === 0 ? (
          <div className="mt-12 text-center text-gray-500">
            No menus published yet.
          </div>
        ) : (
          <div className="mt-10 grid gap-4">
            {menus.map((m) => (
              <Link key={m.id} to={`/menu/${slug}/${m.id}`}
                    data-testid={`menu-card-${m.id}`}
                    className="group">
                <div className="rounded-2xl bg-white p-5 shadow-sm border transition-all hover:shadow-md hover:-translate-y-0.5"
                     style={{ borderColor: `${theme.secondary}33` }}>
                  <div className="flex items-center gap-4">
                    <MenuIcon code={m.menu_type} color={theme.primary} bg={theme.background} />
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg" style={{ color: theme.primary }}>
                        {m.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {m.categories_count} categories · {m.items_count} items
                      </div>
                    </div>
                    <span className="text-sm font-medium group-hover:translate-x-1 transition-transform"
                          style={{ color: theme.secondary }}>
                      View Menu →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <GuestFooter outlet={outlet} theme={theme} />
      </div>
    </div>
  );
}

function MenuIcon({ code, color, bg }) {
  const Icon = ICON_MAP[code] || ICON_MAP.utensils || Utensils;
  return (
    <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
         style={{ background: bg, border: `1px solid ${color}22` }}>
      <Icon className="w-6 h-6" style={{ color }} />
    </div>
  );
}

/* ==================== Single menu view ==================== */

function SingleMenuView({ slug, menuId, outletMeta, theme }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [activeCat, setActiveCat] = useState(null);
  const [fabOpen, setFabOpen] = useState(false);
  const catRefs = useRef({});
  const chipRefs = useRef({});

  useEffect(() => {
    axios.get(`${API}/api/public/menu/outlet-by-slug/${slug}/menu/${menuId}`)
      .then((r) => setDetail(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Menu not found'));
  }, [slug, menuId]);

  const categories = useMemo(
    () => detail?.parsed_data?.categories || [],
    [detail]
  );

  // Filter by search: keep categories that still have any matching AVAILABLE
  // item after the search string is applied.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const withAvailable = categories.map((c) => ({
      ...c,
      items: (c.items || []).filter((it) => it.available !== false),
    }));
    if (!needle) return withAvailable.filter((c) => c.items.length > 0);
    return withAvailable
      .map((c) => ({
        ...c,
        items: c.items.filter((it) =>
          [it.name, it.description, it.subcategory, ...(it.tags || [])]
            .join(' ').toLowerCase().includes(needle)
        ),
      }))
      .filter((c) => c.items.length > 0);
  }, [q, categories]);

  useEffect(() => {
    if (!activeCat && filtered.length) setActiveCat(filtered[0].id);
  }, [filtered, activeCat]);

  // ITER287 — IntersectionObserver drives active-category highlighting
  // while the guest scrolls. Uses a narrow trigger band near the top of
  // the viewport (right below the sticky header) so the active chip
  // switches at the moment the section actually reaches the reader.
  useEffect(() => {
    if (!filtered.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the top-most currently intersecting section.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.dataset.categoryId;
          if (id) setActiveCat(id);
        }
      },
      {
        // Trigger band starts 160px from the top (just below the sticky
        // header) and ends 60% down the viewport — the section becomes
        // "active" as soon as its top crosses that band.
        root: null,
        rootMargin: '-160px 0px -40% 0px',
        threshold: 0,
      }
    );
    Object.values(catRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [filtered]);

  // Keep the active chip visible in the horizontal chip strip.
  useEffect(() => {
    if (!activeCat) return;
    const el = chipRefs.current[activeCat];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeCat]);

  const scrollTo = (cid) => {
    setActiveCat(cid);
    setFabOpen(false);
    catRefs.current[cid]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const fabCategories = useMemo(
    () => filtered.map((c) => ({
      id: c.id, name: c.name, count: c.items.length,
    })),
    [filtered]
  );

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6"
           style={{ background: theme.background }}>
        <div className="text-gray-500">{error}</div>
      </div>
    );
  }
  if (!detail) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500"
                style={{ background: theme.background }}>Loading…</div>;
  }

  return (
    <div className="min-h-screen relative" style={{ background: theme.background }}>
      {/* Sticky header */}
      <div className="sticky top-0 z-20 shadow-sm"
           style={{ background: theme.background, borderBottom: `1px solid ${theme.secondary}33` }}>
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Link to={`/menu/${slug}`} className="p-2 -ml-2 rounded-full hover:bg-black/5"
                  data-testid="back-to-selector">
              <ChevronLeft className="w-5 h-5" style={{ color: theme.primary }} />
            </Link>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg truncate" style={{ color: theme.primary }}>
                {detail.menu.name}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500">
                {outletMeta.outlet.name}
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="mt-3 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search dishes"
                   className="w-full h-10 pl-9 pr-3 rounded-full bg-white border text-sm focus:outline-none"
                   style={{ borderColor: `${theme.secondary}55` }}
                   data-testid="menu-search-input" />
          </div>

          {/* Category chips */}
          <div className="mt-3 overflow-x-auto whitespace-nowrap -mx-4 px-4 pb-1"
               data-testid="category-nav">
            {filtered.map((c) => {
              const active = activeCat === c.id;
              return (
                <button key={c.id}
                        ref={(el) => (chipRefs.current[c.id] = el)}
                        onClick={() => scrollTo(c.id)}
                        className="mr-2 px-3 h-8 rounded-full text-xs font-medium transition-colors"
                        style={{
                          background: active ? theme.primary : '#fff',
                          color: active ? '#fff' : theme.primary,
                          border: `1px solid ${active ? theme.primary : `${theme.primary}33`}`,
                        }}
                        data-testid={`cat-chip-${c.id}`}>
                  {c.name}
                  <span className="ml-1.5 opacity-70 tabular-nums">{c.items.length}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-8 pb-24">
        {filtered.length === 0 ? (
          <div className="text-center text-gray-500 py-12" data-testid="no-dishes-msg">
            No dishes match &ldquo;{q}&rdquo;
          </div>
        ) : (
          filtered.map((c) => (
            <CategorySection
              key={c.id}
              category={c}
              theme={theme}
              refCb={(el) => (catRefs.current[c.id] = el)}
              VegDot={VegDot}
            />
          ))
        )}

        <GuestFooter outlet={outletMeta.outlet} theme={theme}
                     version={detail.menu.version}
                     footerNotes={detail.menu?.footer_notes} />
      </div>

      {/* ITER287 — Floating category selector (Zomato/Swiggy pattern) */}
      {fabCategories.length > 1 && (
        <FloatingCategoryFAB
          categories={fabCategories}
          activeId={activeCat}
          onSelect={scrollTo}
          theme={theme}
          open={fabOpen}
          onOpenChange={setFabOpen}
        />
      )}
    </div>
  );
}

function GuestFooter({ outlet, theme, version, footerNotes }) {
  return (
    <div className="pt-10 pb-2 text-center space-y-2" data-testid="guest-footer">
      {outlet?.logo_url && (
        <div className="mx-auto inline-flex items-center justify-center p-1.5 rounded-xl bg-white shadow-sm"
             style={{ boxShadow: '0 3px 10px rgba(20,61,48,0.08)' }}>
          <img src={outlet.logo_url} alt={outlet.name}
               className="block object-contain rounded-lg max-h-[56px] max-w-[56px] h-auto w-auto" />
        </div>
      )}
      <div className="font-serif text-base" style={{ color: theme.primary }}>
        {outlet?.name}
      </div>
      {footerNotes && (
        <div className="mx-auto max-w-lg text-xs italic leading-snug whitespace-pre-line pt-3 mt-2 border-t"
             style={{ color: theme.secondary,
                      borderColor: `${theme.secondary}33` }}
             data-testid="menu-footer-notes">
          {footerNotes}
        </div>
      )}
      <div className="text-[10px] tracking-[0.25em] text-gray-400 pt-2">
        POWERED BY <span className="font-semibold" style={{ color: theme.primary }}>RESTROPULSE</span>
      </div>
      {version != null && (
        <div className="text-[9px] text-gray-300 tracking-widest">v{version}</div>
      )}
    </div>
  );
}
